import * as THREE from 'three';
import {
  CanvasListenersType,
  DataDrainFunnel,
  DrainFunnelObjectType,
  EditType,
  PointFunnelType,
  RoomType,
  SaveSectorStorageType,
} from '../types';
import { COLORS_CONST } from '../../helpers/styles';
import BasicObject from './BasicObject';
import { TextObject } from './TextObject';
import { Room } from './Room';
import { getCenterMass } from '../js';

interface ConstructorProps {
  points: Array<THREE.Vector3>;
  rooms: Array<Array<number>>;
  Edit: EditType;
  canvasListener?: CanvasListenersType;
  savedDrainFunnel?: SaveSectorStorageType['drainFunnelObjects'];
}
export class DrainFunnelObject extends THREE.Group implements DrainFunnelObjectType {
  private symbolsPoints: Array<string> = [];
  private Edit: EditType;
  private canvasListener?: CanvasListenersType;
  material = new THREE.MeshBasicMaterial({
    color: COLORS_CONST.red,
    side: THREE.DoubleSide,
    // wireframe: true,
  });
  points: Array<THREE.Vector3>;
  rooms: Array<Array<number>>;
  drainObjects: Array<PointFunnelMove> = [];
  planesIntersected: Array<RoomType> = [];

  constructor({ points, rooms, Edit, canvasListener, savedDrainFunnel }: ConstructorProps) {
    super();
    // this.type = 'drain_funnel';
    this.name = 'drain_funnel';
    this.points = points;
    this.rooms = rooms;
    this.Edit = Edit;
    this.canvasListener = canvasListener;

    this.getSymbolsControlsPoints();
    this.buildGroup(savedDrainFunnel);
  }

  buildGroup(savedDrainFunnel?: SaveSectorStorageType['drainFunnelObjects']) {
    this.rooms.forEach(room => {
      const roomDottedName = room.join('');
      let currentSavedDrainFunnel;
      if (savedDrainFunnel && savedDrainFunnel.objects.length) {
        currentSavedDrainFunnel = savedDrainFunnel.objects.find(
          item => item.roomDottedName === roomDottedName
        );
      }
      const controls = room.map(item => this.points[item]);
      // const plane = getPlaneIntersected(controls);

      const center = getCenterMass(controls).setY(0);
      if (currentSavedDrainFunnel) {
        center.x = currentSavedDrainFunnel.position.x;
        center.y = currentSavedDrainFunnel.position.y;
        center.z = currentSavedDrainFunnel.position.z;
      }
      const _room = new Room({
        contour: controls.map(item => item.clone()),
        drainFunnelPosition: center.clone(),
        dottedName: roomDottedName,
      });
      this.planesIntersected.push(_room);

      if (!savedDrainFunnel || currentSavedDrainFunnel) {
        const symbol = this.getFreeSymbol() + 'V';
        const circle = new PointCircle({ symbol });
        circle.position.copy(center);
        this.add(circle);
        const lines = this.addLinesLinking(controls, center);

        const pointFunnel = new PointFunnelMove({
          position: center,
          planeIntersected: _room,
          moveObject: circle,
          symbol,
          linesMove: lines,
        });
        this.Edit.dragObject?.addObjectMove(pointFunnel);
        this.canvasListener?.addObjectsIntersected(pointFunnel);

        this.add(pointFunnel);
        this.drainObjects.push(pointFunnel);
      }
    });
  }
  removeSelectedChildren(uuid: string): number {
    const index = this.drainObjects.findIndex(item => item.uuid === uuid);
    if (index !== -1) {
      this.Edit.dragObject?.removeObjectMove(this.drainObjects[index].uuid);
      this.canvasListener?.removeObjectsIntersected(this.drainObjects[index].uuid);

      this.drainObjects[index].planeIntersected.drainFunnelPosition = undefined;
      this.drainObjects[index].moveObject.removeMesh();
      this.drainObjects[index].removeMesh();
      this.drainObjects.splice(index, 1);
    }
    return this.drainObjects.length;
  }
  updateHeightControlPoints() {
    this.drainObjects.forEach(item => {
      item.position.setY(0);
      item.moveObject.position.setY(0);
    });
  }
  changeHeightFunnel(height: number, uuid: string) {
    this.drainObjects.forEach(item => {
      if (item.uuid === uuid) {
        item.position.y = height;
        item.dragStart();
        item.drag();
        item.dragEnd();
      }
    });
  }
  getParamsData(): Array<DataDrainFunnel> {
    const res: Array<DataDrainFunnel> = [];
    this.drainObjects.forEach(item => {
      res.push({
        heightDrainFunnel: item.position.y,
        symbolFunnel: item.symbol,
        uuid: item.uuid,
      });
    });

    return res;
  }
  removeGroup() {
    this.drainObjects.length = 0;
    this.material.dispose();
    while (this.children.length > 0) {
      if (this.children[0] instanceof PointCircle) {
        this.children[0].removeMesh();
      } else if (this.children[0] instanceof PointFunnelMove) {
        this.Edit.dragObject?.removeObjectMove(this.children[0].uuid);
        this.canvasListener?.removeObjectsIntersected(this.children[0].uuid);
        this.children[0].removeMesh();
      } else if (this.children[0] instanceof LineLinkPoint) {
        this.children[0].removeLineObject();
      }
    }
    while (this.planesIntersected.length > 0) {
      this.planesIntersected[0].geometry.dispose();
      this.planesIntersected[0].material.dispose();
      this.planesIntersected.shift();
    }
    this.parent?.remove(this);
  }

  private addLinesLinking(
    points: Array<THREE.Vector3>,
    center: THREE.Vector3
  ): Array<LineLinkPoint> {
    return points.map(point => {
      const line = new LineLinkPoint({
        startPoint: center.clone().setY(0),
        endPoint: point.clone().setY(0),
      });
      this.add(line);
      return line;
    });
  }
  private getSymbolsControlsPoints = () => {
    for (let i = 65; i < 91; i++) {
      this.symbolsPoints.push(String.fromCharCode(i));
    }
  };
  private getFreeSymbol(): string {
    return this.symbolsPoints.shift() || '$';
  }
}

interface PointFunnelPropsType {
  position: THREE.Vector3;
  planeIntersected: RoomType;
  moveObject: PointCircle;
  symbol: string;
  linesMove: Array<LineLinkPoint>;
}
class PointFunnelMove extends BasicObject implements PointFunnelType {
  private ray = new THREE.Raycaster();
  private directRay = new THREE.Vector3(0, 1, 0);
  private startPosition = new THREE.Vector3();
  planeIntersected: RoomType;
  moveObject: PointCircle;
  symbol: string;
  material = new THREE.MeshBasicMaterial({ transparent: true, opacity: 0 });
  linesMove: Array<LineLinkPoint>;

  constructor({ position, planeIntersected, moveObject, symbol, linesMove }: PointFunnelPropsType) {
    super();
    this.name = 'point_funnel_move';
    this.position.copy(position);
    this.planeIntersected = planeIntersected;
    this.moveObject = moveObject;
    this.symbol = symbol;
    this.linesMove = linesMove;

    this.buildGeometry();
  }
  buildGeometry() {
    super.buildGeometry();
    this.geometry = new THREE.PlaneGeometry(0.6, 0.6);
    this.geometry.applyMatrix4(new THREE.Matrix4().makeRotationX(-Math.PI / 2));
  }
  dragStart() {
    this.startPosition.copy(this.position);
  }
  drag() {
    this.ray.set(this.position.clone().setY(-0.02), this.directRay);
    const inters = this.ray.intersectObject(this.planeIntersected);
    if (inters.length > 0) {
      this.startPosition.copy(this.position);
    } else {
      this.position.copy(this.startPosition);
    }

    this.linesMove.forEach(line => {
      line.updateLineGeometry(this.position, 'startPoint');
    });

    this.moveObject.position.copy(this.position);
  }
  dragEnd() {
    super.dragEnd();
    this.planeIntersected.drainFunnelPosition?.copy(this.position);
  }
  removeMesh() {
    while (this.linesMove.length > 0) {
      this.linesMove[0].removeLineObject();
      this.linesMove.shift();
    }
    super.removeMesh(this);
  }
}

interface PointCirclePropsType {
  symbol: string;
}
class PointCircle extends BasicObject {
  material: THREE.MeshBasicMaterial = new THREE.MeshBasicMaterial({
    color: COLORS_CONST.red,
    transparent: true,
    opacity: 0.8,
  });
  symbol = '$';
  constructor({ symbol }: PointCirclePropsType) {
    super();
    this.type = 'point_circle_funnel';
    this.name = 'point_circle_funnel';
    this.symbol = symbol;

    this.buildGeometry();
  }

  buildGeometry() {
    super.buildGeometry();
    this.geometry = new THREE.CircleGeometry(0.3, 32);
    this.geometry.applyMatrix4(new THREE.Matrix4().makeRotationX(-Math.PI / 2));
    const textObject = new TextObject(this.symbol, 0.12);
    textObject.setStartPosition();
    this.add(textObject);
  }
  removeMesh() {
    const textObj = this.children[0] as TextObject;
    textObj.removeMesh();
    super.removeMesh(this);
  }
}

interface LineLinkPointPropsType {
  startPoint: THREE.Vector3;
  endPoint: THREE.Vector3;
}
class LineLinkPoint extends THREE.Line {
  startPoint: THREE.Vector3;
  endPoint: THREE.Vector3;
  material = new THREE.LineBasicMaterial({
    color: '#3c3c3c',
    linewidth: 1,
    // scale: 0.1,
    // dashSize: 0.003,
    // gapSize: 0.003,
  });
  constructor({ startPoint, endPoint }: LineLinkPointPropsType) {
    super();
    this.type = 'line_linking';
    this.name = 'line_linking';
    this.startPoint = startPoint;
    this.endPoint = endPoint;

    this.buildGeometry();
  }

  buildGeometry() {
    if (this.geometry) {
      this.geometry.dispose();
    }
    this.geometry = new THREE.Geometry().setFromPoints([this.startPoint, this.endPoint]);
    this.computeLineDistances();
  }
  updateLineGeometry(newPos: THREE.Vector3, point: 'startPoint' | 'endPoint') {
    this[point].copy(newPos);
    const geomLine = this.geometry as THREE.Geometry;
    geomLine.vertices[0].copy(newPos);
    geomLine.verticesNeedUpdate = true;
  }
  removeLineObject() {
    if (this.geometry) {
      this.geometry.dispose();
    }
    if (this.material) {
      (this.material as THREE.LineDashedMaterial).dispose();
    }
    this.parent?.remove(this);
  }
}
